export function normalizeName(name) {
    // First, handle initial patterns before converting to lowercase
    // This ensures we can detect capital letter patterns
    let normalized = name;
    // Handle patterns like "A.R." or "J.K." - add space after dots between initials
    normalized = normalized.replace(/\b([A-Z])\.(?=[A-Z]\.?)/g, '$1. ');
    // Handle patterns like "AR" (exactly 2 capitals) at word boundary followed by space and capital letter
    // This ensures we only split when it's clearly initials (e.g., "AR Rahman" but not "USA Today")
    normalized = normalized.replace(/\b([A-Z])([A-Z])(?=\s+[A-Z][a-z])/g, '$1. $2.');
    // Now convert to lowercase and continue with normal processing
    normalized = normalized.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    // Replace common abbreviations and symbols
    const replacements = {
        '&': 'and',
        '+': 'and',
        '@': 'at',
        '№': 'number',
        '№.': 'number',
        'no.': 'number',
        'vol.': 'volume',
        'versus': 'vs',
        'feat.': 'featuring',
        'ft.': 'featuring',
    };
    for (const [key, value] of Object.entries(replacements)) {
        const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Use word boundaries for word characters, spaces for symbols
        const isWordChar = /^[a-zA-Z0-9]+$/.test(key);
        const pattern = isWordChar ? `\\b${escapedKey}\\b` : `\\s${escapedKey}\\s|^${escapedKey}\\s|\\s${escapedKey}$|^${escapedKey}$`;
        normalized = normalized.replace(new RegExp(pattern, 'g'), (match) => {
            // Preserve surrounding spaces when replacing symbols
            const leadingSpace = match.startsWith(' ') ? ' ' : '';
            const trailingSpace = match.endsWith(' ') ? ' ' : '';
            return leadingSpace + value + trailingSpace;
        });
    }
    // Handle apostrophes: remove them but join the words
    normalized = normalized.replace(/(\w+)'s/g, '$1s') // possessives
        .replace(/'/g, ''); // other apostrophes
    // Remove punctuation and symbols, except for hyphens between words
    // Use a Unicode-aware approach to preserve non-Latin characters
    normalized = normalized.replace(/[^\p{L}\p{N}\s-]|\p{Punctuation}/gu, '')
        .replace(/--+/g, '-') // Replace multiple hyphens with single hyphen
        .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
    // Replace multiple spaces with a single space and trim
    normalized = normalized.replace(/\s+/g, ' ').trim();
    // Remove common articles from the beginning (for Latin script names)
    normalized = normalized.replace(/^(the|a|an)\s+/i, '');
    // Remove Roman numerals from the end (up to XX)
    normalized = normalized.replace(/\s+(i|ii|iii|iv|v|vi|vii|viii|ix|x|xi|xii|xiii|xiv|xv|xvi|xvii|xviii|xix|xx)$/i, '');
    // Remove common suffixes (for Latin script names)
    normalized = normalized.replace(/\s+(band|group|orchestra|ensemble|quartet|trio|duo)$/i, '');
    return normalized;
}
/**
 * Folds a name for comparison only: case, Unicode form, and the typographic punctuation MusicBrainz
 * prefers (a U+2010 hyphen in `Hard‐Ons`, a curly apostrophe in `Bourke's`).
 *
 * Distinct from `normalizeName`, which builds an index key and discards information to do it —
 * leading articles, trailing Roman numerals, and trailing band/orchestra/trio words. That makes it
 * the wrong tool for deciding whether two names are the same artist: it reports `Sun Ra` and
 * `Sun Ra Orchestra` as one name. `foldName` discards nothing; it only makes equivalent spellings
 * comparable, so an empty input is the only thing that folds to an empty string.
 */
export function foldName(value) {
    return (value || '')
        .normalize('NFKC')
        .replace(/[\u2010-\u2015]/g, '-')
        .replace(/[\u2018\u2019\u201B]/g, "'")
        .toLowerCase()
        .trim()
        .replace(/\s+/g, ' ');
}
